import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { StreamVideo, StreamCall, StreamTheme, ParticipantView, useCallStateHooks } from "@stream-io/video-react-sdk";
import "@stream-io/video-react-sdk/dist/css/styles.css";
import { ArrowLeft, Users, Crown, Flag, Gift, Square } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLiveSession } from "@/lib/live";
import { LiveChat } from "@/components/LiveChat";
import { TipDialog } from "@/components/TipDialog";

function Player() {
  const { useParticipants, useIsCallLive, useParticipantCount } = useCallStateHooks();
  const host = useParticipants().find((p) => p.roles?.includes("host"));
  const live = useIsCallLive();
  const viewers = Math.max(0, useParticipantCount() - (host ? 1 : 0));
  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-2xl border border-white/10 bg-black">
      {host && live ? <ParticipantView participant={host} trackType="videoTrack" /> : (
        <div className="flex h-full items-center justify-center text-sm text-[#a7a0af]">{live ? "Waiting for the creator's camera…" : "The stream has ended or hasn't started."}</div>
      )}
      <span data-testid="watch-live-state" className={`absolute left-3 top-3 rounded-md px-2 py-1 text-xs font-bold uppercase ${live ? "bg-[#d42be8]" : "bg-white/15"}`}>{live ? "Live" : "Offline"}</span>
      <span data-testid="watch-viewer-count" className="absolute right-3 top-3 flex items-center gap-1 rounded-md bg-black/60 px-2 py-1 text-xs"><Users size={12} /> {viewers}</span>
    </div>
  );
}

function ReportButton({ sessionId }) {
  const [busy, setBusy] = useState(false);
  const report = async () => {
    const reason = window.prompt("What's wrong with this broadcast? Our team will review it.");
    if (!reason || reason.trim().length < 3) return;
    setBusy(true);
    try {
      await api.post(`/live/sessions/${sessionId}/report`, { reason: reason.trim() });
      toast.success("Report sent to the PEEPING team");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <button onClick={report} disabled={busy} data-testid="watch-report-btn" className="flex items-center gap-1 rounded-full border border-white/15 px-3 py-1.5 text-xs text-[#c7c1cf] hover:border-white/40"><Flag size={12} /> Report</button>
  );
}

function TipButton({ creatorId, name }) {
  const [open, setOpen] = useState(false);
  // Checkout opens in a new tab so the stream keeps playing.
  const onCheckout = (url) => {
    const tab = window.open(url, "_blank");
    if (!tab) window.location.assign(url);
    else toast.success("Tip checkout opened in a new tab — the stream keeps playing");
  };
  return (
    <>
      <TipDialog open={open} onOpenChange={setOpen} creator={{ id: creatorId, display_name: name }} onCheckout={onCheckout} />
      <button onClick={() => setOpen(true)} data-testid="watch-tip-btn" className="flex items-center gap-1 rounded-full border border-[#d42be8]/50 px-3 py-1.5 text-xs text-[#f0a8fa]"><Gift size={12} /> Tip</button>
    </>
  );
}

function ReviewEndButton({ sessionId }) {
  const navigate = useNavigate();
  const end = async () => {
    const reason = window.prompt("End this broadcast for everyone? Enter the moderation reason (logged).");
    if (!reason || reason.trim().length < 3) return;
    try {
      await api.post(`/review/live/${sessionId}/end`, { reason: reason.trim() });
      toast.success("Broadcast ended and logged");
      navigate("/app/live");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  return (
    <button onClick={end} data-testid="review-end-live-btn" className="flex items-center gap-1 rounded-full border border-red-400/40 px-3 py-1.5 text-xs text-red-300"><Square size={12} /> End stream</button>
  );
}

export default function LiveWatch() {
  const { user } = useAuth();
  const isReview = user?.role === "platform_owner_review";
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const s = useLiveSession(`/live/sessions/${sessionId}/join`, { viewer: true });
  return (
    <div data-testid="live-watch" className="flex flex-col gap-4 px-4 pb-6 pt-6">
      <button onClick={() => navigate("/app/live")} className="flex items-center gap-1 self-start text-sm text-[#a7a0af]"><ArrowLeft size={16} /> Live</button>
      {s.status === "connecting" && <p className="text-sm text-[#a7a0af]">Joining the stream…</p>}
      {s.status === "error" && (
        <div data-testid="watch-error" className="rounded-2xl glass p-5 text-center">
          <p className="text-sm text-[#c7c1cf]">{s.error}</p>
          {s.code === 403 && <button onClick={() => navigate("/app/membership")} data-testid="watch-get-membership" className="btn-brand mt-4 inline-flex items-center gap-1 rounded-full px-5 py-2 text-sm font-semibold"><Crown size={14} /> See memberships</button>}
        </div>
      )}
      {s.status === "ready" && (
        <>
          <div className="flex items-start justify-between gap-3">
            <div>
              <h1 className="text-xl font-extrabold">{s.creds.session.title}</h1>
              <p className="text-xs text-[#a7a0af]">{s.creds.creator?.display_name} · @{s.creds.creator?.handle}</p>
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              {user?.role === "subscriber" && <TipButton creatorId={s.creds.creator?.id} name={s.creds.creator?.display_name} />}
              {isReview && <ReviewEndButton sessionId={sessionId} />}
              <ReportButton sessionId={sessionId} />
            </div>
          </div>
          <div className="grid min-w-0 grid-cols-1 gap-4">
            <div className="w-full min-w-0">
            <StreamVideo client={s.video}>
              <StreamTheme><StreamCall call={s.call}><Player /></StreamCall></StreamTheme>
            </StreamVideo>
            </div>
            <LiveChat chat={s.chat} channel={s.channel} />
          </div>
        </>
      )}
    </div>
  );
}
